import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import SearchableSelect from './components/SearchableSelect';
import { formatLocalDate } from './timezone';

const METHOD_COLORS = {
  GET: '#61affe',
  POST: '#49cc90',
  PUT: '#fca130',
  DELETE: '#f93e3e',
  PATCH: '#50e3c2',
};

const containerStyle = {
  width: '100%',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const headerStyle = {
  padding: '16px 24px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexShrink: 0,
};

const contentStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
};

const cardStyle = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--bg-input)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
  borderRadius: '4px',
  boxSizing: 'border-box',
  fontSize: '13px',
};

const btnPrimary = {
  padding: '8px 18px',
  background: 'var(--accent-color)',
  border: 'none',
  color: 'var(--text-inverse)',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
};

const btnDanger = {
  padding: '5px 12px',
  background: 'transparent',
  border: '1px solid var(--text-danger, #e57373)',
  color: 'var(--text-danger, #e57373)',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

function MethodBadge({ method }) {
  return (
    <span style={{
      background: METHOD_COLORS[method] || '#aaa',
      color: '#fff',
      fontWeight: 700,
      fontSize: '11px',
      borderRadius: '4px',
      padding: '2px 7px',
      letterSpacing: '0.05em',
    }}>
      {method}
    </span>
  );
}

function CreateRouteForm({ workflows, onCreated }) {
  const [path, setPath] = useState('');
  const [method, setMethod] = useState('POST');
  const [workflowId, setWorkflowId] = useState('');
  const [requireAuth, setRequireAuth] = useState(false);
  const [authKey, setAuthKey] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!path.trim() || !workflowId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/routes`, {
        path: path.trim().replace(/^\//, ''),
        method,
        workflow_id: workflowId,
        auth_key: requireAuth ? (authKey || generateKey()) : null,
        description: description || null,
      });
      onCreated(res.data);
      setPath(''); setMethod('POST'); setWorkflowId(''); setRequireAuth(false); setAuthKey(''); setDescription('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create route');
    } finally {
      setSaving(false);
    }
  };

  const generateKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({ length: 32 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  return (
    <form onSubmit={handleSubmit} style={{ ...cardStyle, borderStyle: 'dashed' }}>
      <h4 style={{ margin: '0 0 14px', fontSize: '14px' }}>New Custom Route</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Path</label>
          <input style={inputStyle} placeholder="orders/process" value={path} onChange={e => setPath(e.target.value)} required />
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            Served at /custom/{path || 'your-path'}
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Method</label>
          <select style={inputStyle} value={method} onChange={e => setMethod(e.target.value)}>
            {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Workflow</label>
        <SearchableSelect
          value={workflowId}
          onChange={setWorkflowId}
          options={workflows.map(wf => ({ value: wf.id, label: wf.name }))}
          placeholder="— select workflow —"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Description (optional)</label>
        <input style={inputStyle} placeholder="What does this endpoint do?" value={description} onChange={e => setDescription(e.target.value)} />
      </div>
      <div style={{ marginBottom: '14px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={requireAuth} onChange={e => setRequireAuth(e.target.checked)} />
          Require API key (X-API-Key header)
        </label>
        {requireAuth && (
          <div style={{ marginTop: '8px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>API Key (leave blank to auto-generate)</label>
            <input style={inputStyle} placeholder="Leave blank to auto-generate" value={authKey} onChange={e => setAuthKey(e.target.value)} />
          </div>
        )}
      </div>
      {error && <div style={{ color: '#e57373', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
      <button type="submit" style={btnPrimary} disabled={saving}>
        {saving ? 'Creating…' : 'Create Route'}
      </button>
    </form>
  );
}

function ApiKeyReveal({ routeData }) {
  const [copied, setCopied] = useState(false);
  const key = routeData.auth_key;
  if (!key) return null;
  const copy = () => { navigator.clipboard.writeText(key); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div style={{ background: 'rgba(76, 175, 80, 0.12)', border: '1px solid rgba(76,175,80,0.4)', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px' }}>
      <div style={{ fontSize: '12px', color: '#81c784', marginBottom: '4px', fontWeight: 600 }}>API Key (shown once — copy now)</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <code style={{ fontSize: '12px', flex: 1, wordBreak: 'break-all' }}>{key}</code>
        <button onClick={copy} style={{ ...btnPrimary, padding: '4px 10px', fontSize: '11px' }}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>
    </div>
  );
}

export default function CustomRouteManager({ token, onBack }) {
  const [routes, setRoutes] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newlyCreated, setNewlyCreated] = useState(null); // holds route data with plain auth_key

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [routesRes, wfRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/routes`, authHeaders),
        axios.get(`${API_BASE_URL}/workflows/`, authHeaders),
      ]);
      setRoutes(routesRes.data);
      setWorkflows(wfRes.data);
    } catch (err) {
      console.error('Failed to load custom routes', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreated = (routeData) => {
    if (routeData.auth_key) setNewlyCreated(routeData);
    fetchData();
  };

  const handleDelete = async (routeId) => {
    if (!(await window.confirm('Delete this custom route?'))) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/routes/${routeId}`, authHeaders);
      setRoutes(r => r.filter(x => x.id !== routeId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete route');
    }
  };

  const copyUrl = (path) => {
    navigator.clipboard.writeText(`${window.location.origin}/custom/${path}`);
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px', padding: '0 6px' }}>←</button>
        )}
        <div>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Custom API Endpoints</h2>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            Expose workflows as REST endpoints at <code>/custom/your-path</code>
          </div>
        </div>
      </div>

      <div style={contentStyle}>
        {newlyCreated && (
          <ApiKeyReveal routeData={newlyCreated} />
        )}

        <CreateRouteForm workflows={workflows} onCreated={handleCreated} />

        {loading ? (
          <div style={{ color: 'var(--text-secondary)', padding: '20px 0' }}>Loading…</div>
        ) : routes.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)', padding: '20px 0', fontSize: '14px' }}>No custom routes yet. Create one above.</div>
        ) : (
          <>
            <h4 style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '20px 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {routes.length} Route{routes.length !== 1 ? 's' : ''}
            </h4>
            {routes.map(route => {
              const wf = workflows.find(w => w.id === route.workflow_id);
              const url = `${window.location.origin}/custom/${route.path}`;
              return (
                <div key={route.id} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <MethodBadge method={route.method} />
                        <code style={{ fontSize: '13px', wordBreak: 'break-all' }}>/custom/{route.path}</code>
                        <button onClick={() => copyUrl(route.path)}
                          style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', padding: '0' }}
                          title="Copy URL">📋</button>
                      </div>
                      {route.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{route.description}</div>}
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Workflow: <strong style={{ color: 'var(--text-primary)' }}>{wf?.name || route.workflow_id}</strong>
                        {route.has_auth && <span style={{ marginLeft: '12px', color: '#ffa726' }}>🔑 Auth required</span>}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary, var(--text-secondary))', marginTop: '4px' }}>
                        Created {formatLocalDate(route.created_at)}
                      </div>
                    </div>
                    <button onClick={() => handleDelete(route.id)} style={{ ...btnDanger, marginLeft: '12px', flexShrink: 0 }}>Delete</button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
